<template>
  <div
    style="z-index: 100"
    class="border-ontick-steel-100 dark:border-ontick-carbon-500 dark:bg-ontick-carbon-500/90 absolute bottom-12 left-0 w-[570px] rounded-lg border bg-gray-50/90 py-3"
  >
    <div v-show="Object.keys(data).length > 0" class="grid grid-cols-7 gap-3">
      <div class="col-span-4">
        <h1
          class="text-ontick-carbon-900 px-3 text-2xl font-semibold uppercase dark:text-gray-200"
        >
          {{ data.symbol_name }}
        </h1>
        <div class="flex items-center space-x-2 px-3">
          <svg
            v-show="Object.keys(data).length > 0"
            width="13"
            height="10"
            viewBox="0 0 13 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="transform transition-all duration-200"
            :class="data.perc_change > 0 ? 'rotate-0' : 'rotate-180'"
          >
            <path
              d="M12.5 10H0L6.25 0L12.5 10Z"
              :fill="data.perc_change > 0 ? '#19D684' : '#F56565'"
            />
          </svg>
          <p
            class="text-ontick-carbon-900 text-lg font-medium dark:text-gray-200"
          >
            {{
              parseFloat(data.close).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
                minimumFractionDigits: decimal_place,
              })
            }}
          </p>
          <div
            class="flex items-center space-x-2"
            :class="
              data.perc_change > 0
                ? 'text-ontick-green-500'
                : 'text-ontick-red-500'
            "
          >
            <span
              >{{
                parseFloat(data.abs_change).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                  minimumFractionDigits: decimal_place,
                })
              }}
            </span>
            <span>({{ parseFloat(data.perc_change).toFixed(2) }}%)</span>
          </div>
        </div>
        <div v-if="chart.length > 0" class="badge-line">
          <v-chart autoresize :options="chartLine(chart, data.perc_change)" />
        </div>
      </div>
      <div class="col-span-3 mr-3">
        <div class="bg-ontick-steel-100 dark:bg-ontick-steel-500 rounded-lg">
          <h3
            class="text-ontick-carbon-900 px-3 pt-3 text-lg font-semibold dark:text-gray-200"
          >
            OHLC
          </h3>
          <div class="px-3 py-3">
            <table
              class="text-ontick-carbon-900 table-fixed text-sm dark:text-gray-200"
            >
              <thead>
                <tr>
                  <th class="w-full"></th>
                  <th></th>
                </tr>
              </thead>
              <tbody class="w-full">
                <tr>
                  <td>(O) Abertura</td>
                  <td class="px-1">
                    {{ convertDecimal(data.open, decimal_place) }}
                  </td>
                </tr>
                <tr>
                  <td>(H) Máxima</td>
                  <td class="px-1">
                    {{ convertDecimal(data.high, decimal_place) }}
                  </td>
                </tr>
                <tr>
                  <td>(L) Mínima</td>
                  <td class="px-1">
                    {{ convertDecimal(data.low, decimal_place) }}
                  </td>
                </tr>
                <tr>
                  <td>(C) Fechamento</td>
                  <td class="px-1">
                    {{ convertDecimal(data.close, decimal_place) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div
          class="bg-ontick-steel-100 dark:bg-ontick-steel-500 mt-3 rounded-lg"
        >
          <div class="px-3 py-3">
            <table
              class="text-ontick-carbon-900 table-fixed text-sm dark:text-gray-200"
            >
              <thead>
                <tr>
                  <th class="w-full"></th>
                  <th></th>
                </tr>
              </thead>
              <tbody class="w-full">
                <tr>
                  <td>Volume</td>
                  <td class="px-2">
                    {{ volume(data.volume) }}
                  </td>
                </tr>
                <tr>
                  <td>Negócios</td>
                  <td class="px-2 text-right">
                    {{ convertDecimal(data.trades, 0) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <placeholder-card-component v-show="Object.keys(data).length == 0" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import ECharts from "@/app/shared/components/chart/echarts";
import PlaceholderCardComponent from "../../../shared/components/placeholder/placeholder-card.component.vue";

export default {
  components: {
    "v-chart": ECharts,
    PlaceholderCardComponent,
  },

  props: {
    decimal_place: {
      type: Number,
      default: 0,
    },
    data: {
      type: Object,
      default: {},
    },
    chart: {
      type: Array,
      default: [],
    },
  },

  computed: {
    ...mapState({
      config: (state) => state.config,
    }),
  },

  methods: {
    convertDecimal(value, number_decimal) {
      let decimal = parseFloat(value).toLocaleString("pt-BR", {
        minimumFractionDigits: number_decimal,
        maximumFractionDigits: number_decimal,
      });
      return decimal;
    },

    volume(value) {
      let new_value = 0;

      if (value) {
        if (value >= 1000000000) {
          new_value = Math.round(value / 1000000000).toFixed(2) + "B";
        } else if (value >= 1000000 && value < 1000000000) {
          new_value = Math.round(value / 1000000).toFixed(2) + "M";
        } else {
          new_value = `${value.toString().substr(0, 3)}K`;
        }
        return new_value;
      }
    },

    chartLine(value, change) {
      let color_selected = " ";
      let color_line = " ";

      let dark =
        this.config.ui_theme == "DARK"
          ? this.config.theme_chart.dark
          : this.config.theme_chart.light;

      if (change > 0) {
        color_selected = "rgba(25, 214, 132, 0.5)";
        color_line = "#19D684";
      } else if (change == 0) {
        color_selected = "RGBA(212,212,212,0.5)";
        color_line = "#D4D4D4";
      } else {
        color_selected = " rgba(235, 77, 75, 0.5)";
        color_line = "#e74c3c";
      }

      let reversedValue = [...value].reverse();

      let option = {
        dataset: {
          dimensions: ["dt", "c"],
          source: reversedValue,
        },

        tooltip: {
          show: false,
          trigger: "axis",
          axisPointer: {
            type: "cross",
          },
        },
        grid: {
          left: "%",
          right: "0%",
          bottom: "10%",
          top: "5%",
          // width: "100%",
          // containLabel: true,
        },
        xAxis: {
          type: "category",
          // inverse: true,
          boundaryGap: false,
          axisLabel: {
            color: "rgba(255, 255, 255, 0.8)",
            fontSize: 10,
            align: "center",
            show: true,
            margin: 0,
            rotate: 40,
          },
          splitLine: {
            lineStyle: {
              color: "rgba(255, 255, 255, 0.2)",
            },
            show: false,
          },

          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },

          minorTick: {
            show: false,
          },
        },
        yAxis: {
          type: "value",
          scale: true,
          nameTextStyle: {
            fontSize: 10,
            show: false,
          },
          axisLabel: {
            color: "rgba(255, 255, 255, 0.8)",
            fontSize: 10,
            inside: true,
            show: false,
            formatter: function (value) {
              value = value.toLocaleString("pt-BR", {
                minimumFractionDigits: 0,
              });
              return value;
            },
          },
          axisLine: {
            lineStyle: {
              color: "rgba(255, 255, 255, 0.2)",
              width: "1",
            },
            show: false,
          },
          splitLine: {
            lineStyle: {
              color: "rgba(255, 255, 255, 0.2)",
            },
            show: false,
          },
          minorSplitLine: {
            show: false,
          },
        },
        series: [
          {
            encode: {
              y: "c",
              x: "dt",
            },

            dimensions: ["dt", "c"],
            type: "line",
            lineStyle: {
              color: color_line,
              opacity: 1,
              width: 1.5,
            },
            showSymbol: false,
            itemStyle: {
              color: color_line,
            },
            clip: true,
            areaStyle: {
              normal: {
                color: new ECharts.graphic.LinearGradient(0, 0, 0, 0.9, [
                  {
                    offset: 0,
                    color: color_selected,
                  },
                  {
                    offset: 1,
                    color: dark.chart_2,
                  },
                ]),
              },
            },
          },
        ],
      };
      return option;
    },
  },
};
</script>

<style>
.badge-line .echarts {
  height: 180px;
  width: 100%;
}
</style>
